const PRINCIPLES = [
  {
    title: 'Overmatch leaves the decisions to you.',
    body: 'We never select a vendor or obligate funds. Every conclusion shows its work. Accountability stays where the law puts it.',
  },
  {
    title: 'Every conclusion shows its work.',
    body: 'Outputs are traceable to the underlying record and the governing text, so a reviewer can check the basis for any claim before it enters a program file.',
  },
]

/** The comparable-set rows: filled cells = KPPs met, of 4. */
const COMPARABLES = [
  { name: 'Tolland', met: 3 },
  { name: 'Brightwater', met: 2 },
  { name: 'Copperline', met: 4 },
]

export default function Capabilities() {
  return (
    <section id="capabilities" className="lp-sec">
      <div className="lp-wrap">
        <h2 className="lp-h2" style={{ margin: '0 0 clamp(36px,4vw,56px)', maxWidth: 820 }}>
          <span style={{ whiteSpace: 'nowrap' }}>Find what closes the gap.</span>{' '}
          <span style={{ whiteSpace: 'nowrap' }}>Route it to the field.</span>
        </h2>

        <div className="lp-cards lp-cap">
          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light">
              <div className="lp-panel__label">FROM THE REQUEST</div>
              <div className="lp-quote">
                &ldquo;We need to detect small drones at the perimeter before
                they&rsquo;re overhead.&rdquo;
              </div>
              <div className="lp-chips lp-chips--bottom">
                <span className="lp-chip lp-chip--solid">KPP · DETECTION RANGE</span>
                <span className="lp-chip lp-chip--solid">KPP · FALSE-ALARM RATE</span>
                <span className="lp-chip lp-chip--outline">+2</span>
              </div>
            </div>
            <div>
              <h3>Understand the gap</h3>
              <p>
                A paragraph becomes measurable performance parameters a program can
                be built around.
              </p>
            </div>
          </article>

          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light" style={{ gap: 6 }}>
              <div className="lp-panel__label" style={{ marginBottom: 4 }}>
                COMPARABLE SET · 4 KPPS
              </div>
              {COMPARABLES.map((c) => (
                <div key={c.name} className="lp-kpp">
                  <span>{c.name}</span>
                  {[0, 1, 2, 3].map((i) => (
                    <i key={i} className={i < c.met ? 'on' : 'off'} />
                  ))}
                </div>
              ))}
              <div className="lp-flag">
                Copperline: foreign ownership flag · pending review
              </div>
            </div>
            <div>
              <h3>Find and vet solutions</h3>
              <p>
                A comparable set, assessed for fit, capacity, eligibility, and risk.
              </p>
            </div>
          </article>

          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light">
              <div className="lp-panel__label">ROUTES TO CAPABILITY · RANKED</div>
              <div className="lp-route lp-route--top">
                <span>Order on existing enterprise vehicle</span>
                <span>~6 wk</span>
              </div>
              <div className="lp-route lp-route--rest">
                <span>Commercial Solutions Opening (OT)</span>
                <span>~4 mo</span>
              </div>
              <div className="lp-route lp-route--rest">
                <span>New FAR Part 12 action</span>
                <span>~7 mo</span>
              </div>
            </div>
            <div>
              <h3>Route to the fastest responsible pathway</h3>
              <p>
                Every route weighed before a new action is opened.
              </p>
            </div>
          </article>

          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light">
              <div className="lp-panel__label">DRAFT · J&amp;A</div>
              <div style={{ lineHeight: 1.55, color: 'var(--body)' }}>
                Only one responsible source can meet the detection-range parameter
                within the fielding window.
              </div>
              <div>
                <span className="lp-cite">FAR 6.302-1</span>
              </div>
              <div className="lp-chips lp-chips--bottom">
                <span className="lp-chip lp-chip--outline">3 SOURCES CITED</span>
                <span className="lp-chip lp-chip--outline">AWAITING KO REVIEW</span>
              </div>
            </div>
            <div>
              <h3>Draft the paperwork, with its sources</h3>
              <p>
                Strategies, estimates, D&amp;Fs, and J&amp;As cited to the governing
                text.
              </p>
            </div>
          </article>

          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light">
              <div className="lp-panel__label">APPROVALS · IN PARALLEL</div>
              <div className="lp-kv">
                <span>Legal review</span><b>DONE</b>
                <span>Cost and pricing</span><b>DONE</b>
                <span>Small business coordinator</span><b className="bronze">IN REVIEW</b>
                <span>Contracting officer</span><b className="dim">QUEUED</b>
              </div>
            </div>
            <div>
              <h3>Route approvals in parallel</h3>
              <p>
                Reviewers work at the same time, against your business rules.
              </p>
            </div>
          </article>

          <article className="lp-card lp-card--grid">
            <div className="lp-panel lp-panel--light">
              <div className="lp-panel__label">WHO SEES THIS PROGRAM</div>
              <div className="lp-kv">
                <span>Expeditionary Systems Office</span><b>CAN EDIT</b>
                <span>Legal and policy reviewers</span><b>CAN REVIEW</b>
                <span>Leadership · Exec</span><b>CAN VIEW</b>
                <span className="dim">Other offices</span><b className="dim">NO ACCESS</b>
              </div>
            </div>
            <div>
              <h3>Collaborate with access you control</h3>
              <p>
                Least privilege by program and portfolio. Contracting, program
                management, legal, and leadership each see only what they&rsquo;re
                authorized to see.
              </p>
            </div>
          </article>
        </div>

        <div className="lp-principles">
          {PRINCIPLES.map((p) => (
            <div key={p.title} style={{ minWidth: 0 }}>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
